import type {
  StaffFulfilmentViewResponse,
  StaffRequestAssignees,
  StaffRequestDetail,
  StaffRequestQuestions,
  StaffRequestQueueFilterValue,
  StaffRequestQueuePage,
  StaffSelf,
} from "@lafayette/schemas";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useRequestsRuntime } from "./runtime";

// The reads of the request pages. A staff answer holds customer data, so nothing stays in the cache after the page lets go
// (`gcTime: 0`), and nothing is written to browser storage. A failed read is not retried by the library: the page shows the error and
// offers a retry. The queue and the detail are read again when the page opens (`refetchOnMount: "always"`), so a list never shows the
// state from before a command.

export const requestKeys = {
  all: ["staff-requests"] as const,
  queue: (filter: StaffRequestQueueFilterValue | null) =>
    ["staff-requests", "queue", filter ?? "ALL"] as const,
  detail: (requestId: string) => ["staff-requests", "detail", requestId] as const,
  questions: (requestId: string) => ["staff-requests", "questions", requestId] as const,
  fulfilment: (requestId: string) => ["staff-requests", "fulfilment", requestId] as const,
  assignees: ["staff-requests", "assignees"] as const,
  // Outside `all`: a command invalidates every request read, and the caller's own identity does not change with it.
  self: ["staff-self"] as const,
};

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnMount: "always",
} as const;

const QUEUE_PAGE_SIZE = 25;

/** MD-R1. The queue, page by page: `fetchNextPage` sends the opaque `nextCursor` of the last page back as `cursor`. */
export function useRequestQueue(filter: StaffRequestQueueFilterValue | null, enabled: boolean) {
  const { api } = useRequestsRuntime();
  return useInfiniteQuery({
    queryKey: requestKeys.queue(filter),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }): Promise<StaffRequestQueuePage> =>
      api.list(
        {
          ...(filter === null ? {} : { filter }),
          ...(pageParam === null ? {} : { cursor: pageParam }),
          limit: QUEUE_PAGE_SIZE,
        },
        { signal },
      ),
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    ...READ_OPTIONS,
  });
}

/** MD-R2. The detail of one request. */
export function useRequestDetail(requestId: string, enabled: boolean) {
  const { api } = useRequestsRuntime();
  return useQuery<StaffRequestDetail>({
    queryKey: requestKeys.detail(requestId),
    queryFn: ({ signal }) => api.get(requestId, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

/** The questions of a request with the asker and the references (staff read). */
export function useRequestQuestions(requestId: string, enabled: boolean) {
  const { questions } = useRequestsRuntime();
  return useQuery<StaffRequestQuestions>({
    queryKey: requestKeys.questions(requestId),
    queryFn: ({ signal }) => questions.list(requestId, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

/** MD-R12. The evidence, the customer updates and the attachments of a request. STAFF ONLY. */
export function useRequestFulfilment(requestId: string, enabled: boolean) {
  const { fulfilment } = useRequestsRuntime();
  return useQuery<StaffFulfilmentViewResponse>({
    queryKey: requestKeys.fulfilment(requestId),
    queryFn: ({ signal }) => fulfilment.get(requestId, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

/**
 * `GET /staff/me`: the caller's own staff id, display name, roles and request capabilities. It is the caller's own identity, not
 * customer data, so it may stay in memory for a few minutes. It is never written to browser storage. A failed read is not retried: the
 * pages then judge the role only and the server judges ownership.
 */
export function useStaffSelf(enabled: boolean) {
  const { api } = useRequestsRuntime();
  return useQuery<StaffSelf>({
    queryKey: requestKeys.self,
    queryFn: ({ signal }) => api.getSelf({ signal }),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
    // A failed read stays failed while the screen is open. Without this, each page that mounts reads again, the loading state returns,
    // and the page unmounts and mounts in a loop.
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/** `GET /staff/request-assignees`: who a request can be given to (the reassign picker). Read again each time the dialog opens. */
export function useRequestAssignees(enabled: boolean) {
  const { api } = useRequestsRuntime();
  return useQuery<StaffRequestAssignees>({
    queryKey: requestKeys.assignees,
    queryFn: ({ signal }) => api.listAssignees({ signal }),
    enabled,
    ...READ_OPTIONS,
  });
}
